// Job-description list — the sidebar's "Job descriptions" destination.
// New postings arrive three ways: pasted text, an uploaded file, or a URL
// (best effort — JS-rendered career portals fail with a "paste instead"
// message). Paste and URL start extracting immediately; files upload first.

import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ClipboardPaste,
  ExternalLink,
  FileText,
  Inbox,
  Link2,
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
  Upload,
  UserPlus,
} from "lucide-react";
import { RESUME_MAX_BYTES } from "@agp/shared";
import { useApi } from "@/hooks/useApi";
import { uploadFile } from "@/lib/api";
import { STATUS_LABEL, displayNameFromEmail, relativeTime } from "@/lib/jobs";
import { jdSourceLabel, type JdListItem } from "@/lib/jds";
import { useOwnerOptions } from "@/hooks/useOwnerOptions";
import { OwnerCell } from "@/components/OwnerCell";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Input, Label, Textarea } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

type CreateMode = "paste" | "file" | "url";

function detectMime(file: File): string | null {
  const ext = file.name.toLowerCase().split(".").pop();
  if (file.type === "application/pdf" || ext === "pdf") return "application/pdf";
  if (
    file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    ext === "docx"
  ) {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (file.type === "text/plain" || ext === "txt") return "text/plain";
  return null;
}

function CreatePanel({ onClose }: { onClose: () => void }) {
  const api = useApi();
  const navigate = useNavigate();
  const [mode, setMode] = useState<CreateMode>("paste");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const submitPaste = async () => {
    setBusy(true);
    setError(null);
    try {
      // A pasted bare link is really a URL job — the model can't browse, so
      // sending it as text can only fail. Route it through the fetch flow.
      const trimmed = text.trim();
      const body = /^https?:\/\/\S+$/i.test(trimmed)
        ? { mode: "url", url: trimmed }
        : { mode: "paste", text };
      const res = await api.post<{ jobId: string }>("/api/jd-jobs", body);
      navigate(`/jds/${res.jobId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the job description.");
      setBusy(false);
    }
  };

  const submitUrl = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ jobId: string }>("/api/jd-jobs", { mode: "url", url });
      navigate(`/jds/${res.jobId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the job description.");
      setBusy(false);
    }
  };

  const submitFile = async (file: File) => {
    setError(null);
    const mimeType = detectMime(file);
    if (!mimeType) {
      setError("Unsupported file type — please upload a PDF, DOCX or TXT posting.");
      return;
    }
    if (file.size > RESUME_MAX_BYTES) {
      setError(`File is too large. The limit is ${Math.round(RESUME_MAX_BYTES / 1024 / 1024)} MB.`);
      return;
    }
    setBusy(true);
    try {
      const created = await api.post<{ jobId: string; uploadUrl: string }>("/api/jd-jobs", {
        mode: "file",
        filename: file.name,
        mimeType,
        sizeBytes: file.size,
      });
      await uploadFile(created.uploadUrl, file);
      await api.post(`/api/jd-jobs/${created.jobId}/extract`);
      navigate(`/jds/${created.jobId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
      setBusy(false);
    }
  };

  const modeButton = (m: CreateMode, icon: React.ReactNode, label: string) => (
    <button
      type="button"
      onClick={() => setMode(m)}
      className={cn(
        "flex items-center gap-2 rounded-md px-3 py-2 text-[13.5px] font-medium transition-colors",
        mode === m
          ? "bg-brand-tint font-semibold text-brand-on"
          : "text-text-2 hover:bg-surface-2 hover:text-text",
      )}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <Card>
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-1">
          {modeButton("paste", <ClipboardPaste size={15} strokeWidth={1.75} aria-hidden />, "Paste text")}
          {modeButton("file", <Upload size={15} strokeWidth={1.75} aria-hidden />, "Upload file")}
          {modeButton("url", <Link2 size={15} strokeWidth={1.75} aria-hidden />, "From link")}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-md px-2 py-1 text-[13px] text-text-2 hover:bg-surface-2 hover:text-text"
          >
            Cancel
          </button>
        </div>

        {mode === "paste" && (
          <>
            <Textarea
              rows={8}
              value={text}
              placeholder="Paste the job posting text here — title, overview, responsibilities, requirements…"
              onChange={(e) => setText(e.target.value)}
            />
            <Button
              className="self-start"
              disabled={!text.trim() || busy}
              onClick={() => void submitPaste()}
            >
              {busy ? "Starting…" : "Extract job description"}
            </Button>
          </>
        )}

        {mode === "file" && (
          <>
            <p className="text-[13.5px] text-text-2">
              Upload the posting as PDF, Word or text. Extraction starts right after the upload.
            </p>
            <Button className="self-start" disabled={busy} onClick={() => fileRef.current?.click()}>
              <Upload size={15} strokeWidth={1.75} aria-hidden />
              {busy ? "Uploading…" : "Choose file"}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void submitFile(file);
                e.target.value = "";
              }}
            />
          </>
        )}

        {mode === "url" && (
          <>
            <div>
              <Label>Posting link</Label>
              <Input
                value={url}
                placeholder="https://…"
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
            <p className="text-[12px] text-text-3">
              Works on pages that serve their text directly. Career portals that render in the
              browser (many ATS sites) can&rsquo;t be read this way — if that happens, copy the
              posting and use Paste text.
            </p>
            <Button
              className="self-start"
              disabled={!url.trim() || busy}
              onClick={() => void submitUrl()}
            >
              {busy ? "Fetching…" : "Extract job description"}
            </Button>
          </>
        )}

        {error && (
          <p className="rounded-md bg-danger-tint px-4 py-2.5 text-[13px] font-medium text-danger-on">
            {error}
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function RowMenu({
  jd,
  onDelete,
  onAssignOwner,
}: {
  jd: JdListItem;
  onDelete: (jd: JdListItem) => void;
  onAssignOwner: (jd: JdListItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const close = useCallback(() => setOpen(false), []);
  const onWrapBlur = (e: React.FocusEvent) => {
    if (!wrapRef.current?.contains(e.relatedTarget as Node)) close();
  };

  return (
    <div ref={wrapRef} className="relative" onBlur={onWrapBlur}>
      <button
        type="button"
        aria-label="Row actions"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-md text-text-2 hover:bg-surface-2 hover:text-text",
          open && "bg-surface-2 text-text",
        )}
      >
        <MoreHorizontal size={16} strokeWidth={1.75} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1 w-40 animate-fade-in overflow-hidden rounded-md border border-border bg-surface py-1.5 shadow-pop"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              navigate(`/jds/${jd.id}`);
            }}
            className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-text hover:bg-surface-2"
          >
            <ExternalLink className="h-4 w-4 text-text-2" strokeWidth={1.75} aria-hidden />
            Open
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onAssignOwner(jd);
            }}
            className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-text hover:bg-surface-2"
          >
            <UserPlus className="h-4 w-4 text-text-2" strokeWidth={1.75} aria-hidden />
            {jd.candidateOwner ? "Change owner" : "Assign owner"}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onDelete(jd);
            }}
            className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-danger-on hover:bg-surface-2"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}

const TH_CLASS =
  "whitespace-nowrap px-5 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.14em] text-text-2";

export function Jds() {
  const api = useApi();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<JdListItem | null>(null);
  // Row whose Owner cell is in edit mode (entered via the "…" menu).
  const [ownerEditId, setOwnerEditId] = useState<string | null>(null);
  const { options: ownerOptions, addOwner } = useOwnerOptions();

  const jdsQuery = useQuery({
    queryKey: ["jd-jobs"],
    queryFn: () => api.get<{ jobs: JdListItem[] }>("/api/jd-jobs"),
    refetchInterval: (query) =>
      query.state.data?.jobs.some((j) => j.status === "extracting") ? 2000 : false,
  });

  const deleteJd = useCallback(
    async (id: string) => {
      await api.delete(`/api/jd-jobs/${id}`);
      void jdsQuery.refetch();
    },
    [api, jdsQuery],
  );

  const jds = jdsQuery.data?.jobs;
  const filtered = useMemo(() => {
    if (!jds) return [];
    const q = search.trim().toLowerCase();
    if (!q) return jds;
    return jds.filter(
      (j) =>
        (j.title ?? "").toLowerCase().includes(q) ||
        (j.company ?? "").toLowerCase().includes(q) ||
        jdSourceLabel(j).toLowerCase().includes(q) ||
        j.ownerEmail.toLowerCase().includes(q),
    );
  }, [jds, search]);

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="mb-1">Job descriptions</h2>
          <p className="text-[14px] text-text-2">
            Client postings turned into AGP-branded job descriptions
            {jds ? ` — ${jds.length} total` : ""}.
          </p>
        </div>
        <Button onClick={() => setCreating((v) => !v)}>
          <Plus size={15} strokeWidth={1.75} aria-hidden />
          New job description
        </Button>
      </header>

      {creating && <CreatePanel onClose={() => setCreating(false)} />}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete this job description?"
        body={
          pendingDelete ? (
            <>
              <span className="font-semibold text-text">
                {pendingDelete.title ?? jdSourceLabel(pendingDelete)}
              </span>{" "}
              and every generated document for it are removed. This cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Delete"
        danger
        onConfirm={() => {
          if (pendingDelete) void deleteJd(pendingDelete.id);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />

      {!jdsQuery.isLoading && (jds?.length ?? 0) === 0 && !creating ? (
        <EmptyState
          icon={<Inbox className="h-5 w-5" strokeWidth={1.75} aria-hidden />}
          title="No job descriptions yet"
          body="Paste a posting, upload a file or drop in a link — extraction usually takes under 30 seconds."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus size={15} strokeWidth={1.75} aria-hidden />
              New job description
            </Button>
          }
        />
      ) : (
        <Card>
          <CardBody className="px-0 py-0">
            <div className="flex items-center gap-2 border-b border-border px-5 py-3">
              <Search size={15} strokeWidth={1.75} className="shrink-0 text-text-2" aria-hidden />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by position, company or source…"
                aria-label="Search job descriptions"
                className="h-8 max-w-sm border-0 bg-transparent px-1 focus:outline-none"
              />
            </div>
            {!jdsQuery.isLoading && filtered.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-text-2">
                Nothing matches “{search}”.
              </p>
            ) : (
              <div className="overflow-x-auto xl:overflow-x-visible">
                {/* Fixed layout so the table can never outgrow the card:
                    narrow columns are pinned, Position/Company/Source share
                    the rest and truncate. Below xl the wrapper scrolls. */}
                <table className="w-full min-w-[960px] table-fixed border-collapse text-[14px] xl:min-w-0">
                  <thead>
                    <tr className="border-b border-border">
                      <th className={TH_CLASS}>Position</th>
                      <th className={TH_CLASS}>Company</th>
                      <th className={TH_CLASS}>Source</th>
                      <th className={cn(TH_CLASS, "w-28")}>Created</th>
                      <th className={cn(TH_CLASS, "w-28")}>Added by</th>
                      <th className={cn(TH_CLASS, "w-40")}>Owner</th>
                      <th className={cn(TH_CLASS, "w-40")}>Status</th>
                      <th className={cn(TH_CLASS, "w-12")} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {jdsQuery.isLoading
                      ? Array.from({ length: 4 }).map((_, i) => (
                          <tr key={i}>
                            {Array.from({ length: 7 }).map((_, c) => (
                              <td key={c} className="px-5 py-3.5">
                                <Skeleton className="h-3.5 w-24" />
                              </td>
                            ))}
                            <td className="px-5 py-3.5" />
                          </tr>
                        ))
                      : filtered.map((jd) => {
                          const status = STATUS_LABEL[jd.status];
                          return (
                            <tr key={jd.id} className="group transition-colors hover:bg-surface-2">
                              <td className="px-5 py-3">
                                <Link
                                  to={`/jds/${jd.id}`}
                                  className="flex min-w-0 items-center gap-2.5 no-underline"
                                >
                                  <FileText
                                    size={15}
                                    strokeWidth={1.75}
                                    className="shrink-0 text-brand-on"
                                    aria-hidden
                                  />
                                  <span className="min-w-0">
                                    <span className="block truncate font-semibold text-text">
                                      {jd.title ?? "—"}
                                    </span>
                                    {jd.outputCount > 0 && (
                                      <span className="block text-[12px] text-text-2">
                                        {jd.outputCount} document{jd.outputCount === 1 ? "" : "s"}
                                      </span>
                                    )}
                                  </span>
                                </Link>
                              </td>
                              <td className="truncate px-5 py-3 text-text">
                                {jd.company ?? "—"}
                              </td>
                              <td className="truncate px-5 py-3 text-text-2">
                                {jdSourceLabel(jd)}
                              </td>
                              <td className="whitespace-nowrap px-5 py-3 text-text-2">
                                {relativeTime(jd.createdAt)}
                              </td>
                              <td className="truncate px-5 py-3 text-text-2">
                                {displayNameFromEmail(jd.ownerEmail) ?? jd.ownerEmail}
                              </td>
                              <td className="whitespace-nowrap px-5 py-3">
                                <OwnerCell
                                  value={jd.candidateOwner}
                                  patchPath={`/api/jd-jobs/${jd.id}/candidate-owner`}
                                  invalidateKey="jd-jobs"
                                  options={ownerOptions}
                                  onCreate={addOwner}
                                  editing={ownerEditId === jd.id}
                                  onClose={() => setOwnerEditId(null)}
                                />
                              </td>
                              <td className="whitespace-nowrap px-5 py-3">
                                <Badge variant={status.variant}>{status.label}</Badge>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <RowMenu
                                  jd={jd}
                                  onDelete={setPendingDelete}
                                  onAssignOwner={(j) => setOwnerEditId(j.id)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
